/* eslint-disable no-unused-vars */

import moment from 'moment';
import { mapState } from 'vuex';
import defaultAvatar from '@/assets/images/default-avatar.png';
import accountSocialAvatar from '@/components/account/account-social-avatar/account-social-avatar.vue';

const LEVEL_BRONZE_THRESHOLD = 10;
const LEVEL_SILVER_THRESHOLD = 135;
const LEVEL_GOLD_THRESHOLD = 915;
const LEVEL_PLATINUM_THRESHOLD = 4950;
const LEVEL_DIAMOND_THRESHOLD = 30000;
const LEVEL_BLACK_THRESHOLD = 375000;

const LEVEL_MAP = {
  LEVEL_LOADING: { num: 100, text: '', progressColor: 'transparent', currentColor: 'transparent' },
  LEVEL_BLUE_THRESHOLD: {
    num: 1,
    text: 'Novice',
    next: LEVEL_BRONZE_THRESHOLD,
    progressColor: '#e2a779',
    currentColor: '#0c96ae',
  },
  LEVEL_BRONZE_THRESHOLD: {
    num: 2,
    text: 'Prognosticator',
    next: LEVEL_SILVER_THRESHOLD,
    progressColor: '#cdcdcd',
    currentColor: '#e2a779',
  },
  LEVEL_SILVER_THRESHOLD: {
    num: 3,
    text: 'Diviner',
    next: LEVEL_GOLD_THRESHOLD,
    progressColor: '#fadf7c',
    currentColor: '#cdcdcd',
  },
  LEVEL_GOLD_THRESHOLD: {
    num: 4,
    text: 'Soothsayer',
    next: LEVEL_PLATINUM_THRESHOLD,
    progressColor: '#4f4e4e',
    currentColor: '#fadf7c',
  },
  LEVEL_PLATINUM_THRESHOLD: {
    num: 5,
    text: 'Fortune Teller',
    next: LEVEL_DIAMOND_THRESHOLD,
    progressColor: '#88b9ce',
    currentColor: '#4f4e4e',
  },
  LEVEL_DIAMOND_THRESHOLD: {
    num: 6,
    text: 'Prophet',
    next: LEVEL_BLACK_THRESHOLD,
    progressColor: '#000000',
    currentColor: '#88b9ce',
  },
  LEVEL_BLACK_THRESHOLD: { num: 7, text: 'Oracle', next: null, currentColor: '#000000' },
};

const ACTIVE_COLOR = '#07a0bb';
const ACTIVE_ROUTES = ['portfolio'];

const socialLevelMixin = {
  components: {
    accountSocialAvatar,
  },
  computed: {
    ...mapState('profile', ['profileDetail']),
    ...mapState('user', ['userData']),
    // computed properties are in user context
    level() {
      if (this.isSocialReady) {
        return this.getLevelByPredictions(this.profileDetail.profitablePredictions);
      }
      return LEVEL_MAP.LEVEL_LOADING;
    },
    activeFor() {
      return moment.utc(this.userData.dateRegistered).fromNow(true);
    },
    toNextLevel() {
      const next = this.level.next;
      if (next !== null) return (next - this.profileDetail.profitablePredictions).toLocaleString();
      return null;
    },
    progressPercentage() {
      const next = this.level.next;
      const predictionsNormalized = 100 * this.profileDetail.profitablePredictions;
      if (next !== null) return Math.floor(predictionsNormalized / next);
      return 0;
    },
    progressColor() {
      return this.level.progressColor ? this.level.progressColor : null;
    },
    progressInfoText() {
      return `You are ${this.progressPercentage}% of the way to Level ${this.level.num + 1}`;
    },
    levelColor() {
      return this.level.currentColor;
    },
    levelColorWithRespectToNav() {
      return this.levelColor;
    },
    isTopLevelTrader() {
      return this.toNextLevel === null;
    },
    userAvatar() {
      if (this.isSocialReady && this.userData.avatarImage) {
        return this.userData.avatarImage;
      }
      return defaultAvatar;
    },
    isSocialReady() {
      return (
        this.userData &&
        this.userData.userName !== undefined &&
        this.profileDetail &&
        this.profileDetail.userGuid !== undefined
      );
    },
  },
  methods: {
    getProgressInfoTextForLevel(num, profitablePredictions) {
      const nextLevel = this.getLevelByNumber(num);
      const predictionsNormalized = 100 * profitablePredictions;
      if (num === LEVEL_MAP.LEVEL_BLACK_THRESHOLD.num) {
        return "You've reached Top Level.";
      }

      const progressPercentage = Math.floor(predictionsNormalized / nextLevel.next);
      return `You are ${progressPercentage}% of the way to Level ${num + 1}`;
    },
    getThresholdByNumber(num) {
      if (num === LEVEL_MAP.LEVEL_BLACK_THRESHOLD) return '-';
      const previousLevel = this.getLevelByNumber(num - 1);
      const currentLevel = this.getLevelByNumber(num);

      return currentLevel.next - previousLevel.next;
    },
    getLevelByNumber(num) {
      if (num === LEVEL_MAP.LEVEL_BLUE_THRESHOLD.num) return LEVEL_MAP.LEVEL_BLUE_THRESHOLD;
      if (num === LEVEL_MAP.LEVEL_BRONZE_THRESHOLD.num) return LEVEL_MAP.LEVEL_BRONZE_THRESHOLD;
      if (num === LEVEL_MAP.LEVEL_SILVER_THRESHOLD.num) return LEVEL_MAP.LEVEL_SILVER_THRESHOLD;
      if (num === LEVEL_MAP.LEVEL_GOLD_THRESHOLD.num) return LEVEL_MAP.LEVEL_GOLD_THRESHOLD;
      if (num === LEVEL_MAP.LEVEL_PLATINUM_THRESHOLD.num) return LEVEL_MAP.LEVEL_PLATINUM_THRESHOLD;
      if (num === LEVEL_MAP.LEVEL_DIAMOND_THRESHOLD.num) return LEVEL_MAP.LEVEL_DIAMOND_THRESHOLD;
      return LEVEL_MAP.LEVEL_BLACK_THRESHOLD;
    },
    getLevelByPredictions(threshold) {
      if (!threshold) return LEVEL_MAP.LEVEL_BLUE_THRESHOLD;
      if (threshold < LEVEL_BRONZE_THRESHOLD) return LEVEL_MAP.LEVEL_BLUE_THRESHOLD;
      if (threshold < LEVEL_SILVER_THRESHOLD) return LEVEL_MAP.LEVEL_BRONZE_THRESHOLD;
      if (threshold < LEVEL_GOLD_THRESHOLD) return LEVEL_MAP.LEVEL_SILVER_THRESHOLD;
      if (threshold < LEVEL_PLATINUM_THRESHOLD) return LEVEL_MAP.LEVEL_GOLD_THRESHOLD;
      if (threshold < LEVEL_DIAMOND_THRESHOLD) return LEVEL_MAP.LEVEL_PLATINUM_THRESHOLD;
      if (threshold < LEVEL_BLACK_THRESHOLD) return LEVEL_MAP.LEVEL_DIAMOND_THRESHOLD;
      return LEVEL_MAP.LEVEL_BLACK_THRESHOLD;
    },
  },
};

export default socialLevelMixin;
